import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Spinner,
} from '../../../shared/components/ui';
import { jobsApi } from '../../../shared/services/jobsApi';
import { formatCurrency, formatSqFt, formatDate } from '../../../shared/utils/formatters';

function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <svg
        className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );
}

function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900"
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function JobsTable({ jobs, onRowClick }) {
  if (jobs.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="w-12 h-12 mx-auto text-gray-300 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <p className="text-gray-500 dark:text-gray-400">No jobs found matching your filters.</p>
      </div>
    );
  }

  const getOutcomeBadge = (outcome) => {
    switch (outcome) {
      case 'profitable':
        return <Badge variant="green">Profitable</Badge>;
      case 'break-even':
        return <Badge variant="yellow">Break-even</Badge>;
      case 'loss':
        return <Badge variant="red">Loss</Badge>;
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800">
            <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Project
            </th>
            <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Type
            </th>
            <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Location
            </th>
            <th className="text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Size
            </th>
            <th className="text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Final Cost
            </th>
            <th className="text-right py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Margin
            </th>
            <th className="text-center py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Outcome
            </th>
            <th className="text-left py-3 px-4 font-medium text-gray-500 dark:text-gray-400 text-sm">
              Completed
            </th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job) => (
            <tr
              key={job.id}
              onClick={() => onRowClick(job.id)}
              className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
            >
              <td className="py-4 px-4">
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{job.projectName}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{job.customer}</p>
                </div>
              </td>
              <td className="py-4 px-4">
                <Badge variant="blue" size="sm">
                  {job.jobType}
                </Badge>
              </td>
              <td className="py-4 px-4 text-gray-700 dark:text-gray-200">{job.location}</td>
              <td className="py-4 px-4 text-right text-gray-700 dark:text-gray-200">
                {formatSqFt(job.squareFootage)}
              </td>
              <td className="py-4 px-4 text-right font-medium text-gray-900 dark:text-gray-100">
                {formatCurrency(job.finalCost)}
              </td>
              <td className="py-4 px-4 text-right text-gray-700 dark:text-gray-200">
                {job.margin ? `${job.margin}%` : '—'}
              </td>
              <td className="py-4 px-4 text-center">
                {getOutcomeBadge(job.outcome)}
              </td>
              <td className="py-4 px-4 text-gray-500 dark:text-gray-400 text-sm">
                {formatDate(job.completedDate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(i);
  }

  return (
    <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 pt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </Button>
      <div className="flex gap-2">
        {pages.map((page) => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${page === currentPage
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
          >
            {page}
          </button>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Next
      </Button>
    </div>
  );
}

export function JobList() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [jobTypes, setjobTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [jobType, setjobType] = useState('');
  const [location, setLocation] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);

  // Location options (could come from API)
  const locationOptions = [
    { value: 'Cincinnati, OH', label: 'Cincinnati, OH' },
    { value: 'Columbus, OH', label: 'Columbus, OH' },
    { value: 'Cleveland, OH', label: 'Cleveland, OH' },
    { value: 'Indianapolis, IN', label: 'Indianapolis, IN' },
    { value: 'Louisville, KY', label: 'Louisville, KY' },
    { value: 'Lexington, KY', label: 'Lexington, KY' },
  ];

  // Load building types
  useEffect(() => {
    async function loadjobTypes() {
      try {
        const types = await jobsApi.getJobTypes();
        setjobTypes(
          types.map((t) => ({ value: t, label: t }))
        );
      } catch (err) {
        console.error('Failed to load building types:', err);
      }
    }
    loadjobTypes();
  }, []);

  // Load jobs
  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await jobsApi.getJobs({
        search,
        jobType,
        location,
        page: currentPage,
        limit: 10,
      });
      setJobs(result.jobs);
      setTotalPages(result.totalPages);
      setTotalJobs(result.total);
    } catch (err) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setIsLoading(false);
    }
  }, [search, jobType, location, currentPage]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, jobType, location]);

  const handleRowClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  const handleClearFilters = () => {
    setSearch('');
    setjobType('');
    setLocation('');
  };

  const hasFilters = search || jobType || location;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Historical Jobs</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Browse and analyze your past projects to inform future quotes.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search projects or customers..."
            />
            <SelectInput
              value={jobType}
              onChange={setjobType}
              options={jobTypes}
              placeholder="All Job Types"
            />
            <SelectInput
              value={location}
              onChange={setLocation}
              options={locationOptions}
              placeholder="All Locations"
            />
            {hasFilters && (
              <Button variant="ghost" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              {isLoading ? 'Loading...' : `${totalJobs} Jobs`}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 mb-4">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              <JobsTable jobs={jobs} onRowClick={handleRowClick} />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default JobList;
